import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
// ✅ Import with different names to avoid conflict
import { 
  addToWishlist as addToWishlistAPI, 
  getWishlistItems, 
  removeFromWishlist as removeFromWishlistAPI, 
  updateWishlistItem 
} from '../services/wishListService';

// ✅ Async thunks with proper naming
export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getWishlistItems();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch wishlist');
    }
  }
);

// ✅ Renamed to addToWishlistAsync to avoid conflict
export const addToWishlistAsync = createAsyncThunk(
  'wishlist/addToWishlist',
  async (wishlistItem, { rejectWithValue }) => {
    try {
      const response = await addToWishlistAPI(wishlistItem); // ✅ Use API function
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add to wishlist');
    }
  }
);

// ✅ Renamed to removeFromWishlistAsync to avoid conflict
export const removeFromWishlistAsync = createAsyncThunk(
  'wishlist/removeFromWishlist',
  async (productId, { rejectWithValue }) => {
    try {
      await removeFromWishlistAPI(productId); // ✅ Use API function
      return productId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove from wishlist');
    }
  }
);

const initialState = {
  items: [],
  loading: false,
  error: null,
  totalItems: 0,
  totalAmount: 0
};

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    clearWishlistError: (state) => {
      state.error = null;
    },
    clearWishlist: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalAmount = 0;
    }
  },
  extraReducers: (builder) => {
    builder
      // ✅ Add to wishlist - using renamed async thunk
      .addCase(addToWishlistAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addToWishlistAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
        state.totalItems = state.items.length;
        state.totalAmount = state.items.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
      })
      .addCase(addToWishlistAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // ✅ Fetch wishlist
      .addCase(fetchWishlist.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload?.data || action.payload || [];
        state.totalItems = state.items.length;
        state.totalAmount = state.items.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
      })
      .addCase(fetchWishlist.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // ✅ Remove from wishlist - using renamed async thunk
      .addCase(removeFromWishlistAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFromWishlistAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item.id !== action.payload);
        state.totalItems = state.items.length;
        state.totalAmount = state.items.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
      })
      .addCase(removeFromWishlistAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearWishlistError, clearWishlist } = wishlistSlice.actions;

// ✅ Selectors
export const selectWishlistItems = (state) => state.wishlist.items;
export const selectWishlistLoading = (state) => state.wishlist.loading;
export const selectWishlistError = (state) => state.wishlist.error;
export const selectIsInWishlist = (productId) => (state) =>
  state.wishlist.items.some(item => 
    item.productId === productId || item.ProductId === productId
  );

export default wishlistSlice.reducer;